import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Mail, Lock, User, Eye, EyeOff, ArrowLeft } from "lucide-react";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created successfully!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container" data-testid="register-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="auth-card"
      >
        <button
          onClick={() => navigate("/")}
          className="flex items-center text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back to map
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
            <MapPin className="text-sky-600" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Create account</h1>
            <p className="text-slate-500 text-sm">Join the Loocator for India community</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-slate-700">Full Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-10 h-12 rounded-xl"
                data-testid="register-name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-700">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 rounded-xl"
                data-testid="register-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-700">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 h-12 rounded-xl"
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 h-12 rounded-xl"
                data-testid="register-confirm-password"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold mt-2"
            data-testid="register-submit"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating account...
              </span>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-slate-500">or continue with</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={loginWithGoogle}
          className="w-full h-12 rounded-full border-slate-200 hover:bg-slate-50 font-medium"
          data-testid="google-register"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={() =>
            toast.info("Apple Sign-In is coming soon — please use Google or email for now.")
          }
          className="w-full h-12 rounded-full mt-3 bg-black text-white hover:bg-black/90 border-black font-medium"
          data-testid="apple-register"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M16.365 1.43c0 1.14-.457 2.234-1.204 3.02-.79.844-2.076 1.5-3.14 1.415-.13-1.12.42-2.257 1.176-3.02.833-.848 2.245-1.474 3.168-1.415zM20.5 17.317c-.36.833-.797 1.62-1.31 2.36-.7 1.006-1.279 1.702-1.727 2.087-.694.61-1.437.923-2.234.94-.572 0-1.262-.163-2.066-.492-.808-.328-1.55-.492-2.229-.492-.71 0-1.475.164-2.297.492-.822.33-1.485.5-1.99.517-.767.033-1.528-.288-2.286-.964-.485-.418-1.09-1.14-1.813-2.166-.775-1.098-1.412-2.373-1.91-3.827-.535-1.57-.803-3.088-.803-4.56 0-1.686.365-3.14 1.096-4.36.575-.98 1.34-1.751 2.298-2.316.958-.567 1.993-.856 3.107-.874.607 0 1.402.187 2.39.554.985.368 1.618.554 1.897.554.208 0 .91-.218 2.098-.65 1.124-.402 2.072-.568 2.848-.503 2.104.17 3.685 1 4.738 2.494-1.882 1.14-2.812 2.735-2.794 4.783.017 1.596.596 2.923 1.735 3.977.516.492 1.093.87 1.734 1.14-.14.402-.286.786-.44 1.15z"/>
          </svg>
          Continue with Apple
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={() =>
            toast.info("Yahoo Sign-In is coming soon — please use Google or email for now.")
          }
          className="w-full h-12 rounded-full mt-3 border-slate-200 hover:bg-slate-50 font-medium text-[#5F01D1]"
          data-testid="yahoo-register"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M13.13 14.24 15.66 8h3.31l-4.94 11.44a19.03 19.03 0 0 1-2.24 4.06h-3.32l1.63-3.55L6.66 8h3.4l3.07 6.24zm5.13-2.34a1.9 1.9 0 1 1 0-3.79 1.9 1.9 0 0 1 0 3.79zM19.66.72l-4.13 5.05h-3.62L15.53.72h4.13z"/>
          </svg>
          Continue with Yahoo
        </Button>

        <p className="text-center mt-6 text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="text-sky-600 hover:text-sky-700 font-medium">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
